import { useEffect, useMemo, useRef, useState } from 'react';
import { computeFSI } from './fsiCalc';
import './FSITest.css';
import { hasBlocks } from '../labEngine';

const n = (s) => (s === '' || s == null ? NaN : Number(s));
const SPEC = ['I', 'II'];

// Free swell index — IS 2720 (Part 40):1977. Two specimens, each read in distilled water (Vd) and in kerosene (Vk).
// Project details live in the portal; this form only holds the test readings.
export default function FSITest({ onReport }) {
  const [rows, setRows] = useState(() => SPEC.map(() => ({ vd: '', vk: '' })));
  const [settle, setSettle] = useState('');
  const setCell = (i, k) => (e) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const complete = rows.every((r) => Number.isFinite(n(r.vd)) && Number.isFinite(n(r.vk))) && Number.isFinite(n(settle));
  const dirty = settle !== '' || rows.some((r) => r.vd !== '' || r.vk !== '');

  const inputs = useMemo(() => complete
    ? { specimens: rows.map((r) => ({ vdCc: n(r.vd), vkCc: n(r.vk) })), settleHours: n(settle) } : null, [complete, rows, settle]);
  const { result, issues } = useMemo(() => (inputs ? computeFSI(inputs) : { result: null, issues: [] }), [inputs]);
  const ready = !!result && !hasBlocks(issues);

  const missing = [
    ...rows.flatMap((r, i) => [[`Vd, specimen ${SPEC[i]}`, r.vd], [`Vk, specimen ${SPEC[i]}`, r.vk]]),
    ['settling time', settle],
  ].filter(([, v]) => !Number.isFinite(n(v))).map(([l]) => l);
  const hint = ready ? '' : !dirty ? 'enter the readings'
    : missing.length ? `fill ${missing[0]}${missing.length > 1 ? ` (+${missing.length - 1} more)` : ''}`
      : (issues.find((i) => i.level === 'block')?.msg || '');

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty',
    ready, dirty, hint,
    summary: ready ? `FSI ${result.fsiPct.toFixed(1)} %` : '',
    payload: ready ? { inputs, result, issues } : null,
  }), [ready, dirty, hint, result, issues, inputs]);

  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  return (
    <div className="fsi">
      <section>
        <div className="fsi-sec-head"><h4>Settling</h4><span className="fsi-muted">soil left to settle in both cylinders</span></div>
        <label className="fsi-field fsi-small">Settling time (h) *
          <input className="fsi-input" type="number" step="0.5" min="0" value={settle} onChange={(e) => setSettle(e.target.value)} />
          <span className="fsi-muted">minimum 24 h</span>
        </label>
      </section>

      <section>
        <div className="fsi-sec-head"><h4>Cylinder readings</h4><span className="fsi-muted">volume of the settled soil, in cc</span></div>
        <div className="fsi-table">
          <div className="fsi-row fsi-head"><span /><span>Specimen I</span><span>Specimen II</span></div>
          <div className="fsi-row">
            <span className="fsi-lab"><b>Vd</b> in distilled water</span>
            {rows.map((r, i) => (
              <input key={i} className="fsi-input" type="number" step="0.5" min="0" aria-label={`Vd specimen ${SPEC[i]}`} value={r.vd} onChange={setCell(i, 'vd')} />
            ))}
          </div>
          <div className="fsi-row">
            <span className="fsi-lab"><b>Vk</b> in kerosene</span>
            {rows.map((r, i) => (
              <input key={i} className="fsi-input" type="number" step="0.5" min="0" aria-label={`Vk specimen ${SPEC[i]}`} value={r.vk} onChange={setCell(i, 'vk')} />
            ))}
          </div>
          <div className="fsi-row">
            <span className="fsi-lab"><b>FSI %</b> = (Vd − Vk) / Vk × 100</span>
            {SPEC.map((_, i) => <span key={i} className="fsi-calc">{result ? result.values[i].toFixed(2) : '—'}</span>)}
          </div>
        </div>
      </section>

      {result && (
        <section className="fsi-result">
          <div className="fsi-stat"><span>Free swell index (average)</span><b>{result.fsiPct.toFixed(1)} %</b></div>
        </section>
      )}

      {issues.length > 0 && (
        <ul className="fsi-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'fsi-err' : 'fsi-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="fsi-muted">({it.clause})</span>
            </li>
          ))}
        </ul>
      )}
      {dirty && missing.length > 0 && <p className="fsi-missing"><b>Still to fill:</b> {missing.join(', ')}</p>}
    </div>
  );
}
